<template>
  <!-- Arranges the resume's sections and hides the ones you do not want. Drag a row (by its handle) or use the arrows; the preview, the section
       icons beside the form and the JSON (content.settings.section_order and hidden_sections) all follow. Personal details is the header of the
       resume, so it stays first. -->
  <div>
    <ul class="space-y-1.5" aria-label="Sections">
      <li class="flex items-center gap-2.5 h-11 pl-3 pr-3 rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-[#161b22]">
        <span class="w-6 h-6 flex-shrink-0 inline-flex items-center justify-center text-gray-300 dark:text-gray-600" aria-hidden="true">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" :d="LOCK"/></svg>
        </span>
        <svg class="w-[18px] h-[18px] flex-shrink-0 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" :d="ICONS.basics"/></svg>
        <span class="flex-1 min-w-0 truncate text-[13px] font-medium text-gray-700 dark:text-gray-200">Personal details</span>
        <span class="text-[11px] font-medium text-gray-400 dark:text-gray-500">Always first</span>
      </li>
    </ul>

    <draggable v-model="keys" :item-key="keyOf" tag="ul" class="space-y-1.5 mt-1.5" aria-label="Sections you can move" :animation="200"
      handle=".section-handle" ghost-class="section-row-ghost" chosen-class="section-row-chosen" drag-class="section-row-drag">
      <template #item="{ element: key, index }">
        <li class="section-row flex items-center gap-2.5 h-11 pl-3 pr-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128]" :data-section="key">
          <span class="section-handle w-6 h-6 flex-shrink-0 inline-flex items-center justify-center rounded-md cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 touch-none"
            :title="`Drag to move ${labelFor(key)}`" aria-hidden="true">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>
          </span>
          <svg class="w-[18px] h-[18px] flex-shrink-0 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" :d="iconFor(key)"/></svg>
          <span class="flex-1 min-w-0 truncate text-[13px] font-medium" :class="isHidden(key) ? 'text-gray-400 dark:text-gray-500' : 'text-gray-800 dark:text-gray-100'">{{ labelFor(key) }}</span>
          <span v-if="note(key)" class="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">{{ note(key) }}</span>
          <span class="flex-shrink-0 w-5 text-right text-[11px] tabular-nums text-gray-400 dark:text-gray-500" aria-hidden="true">{{ index + 1 }}</span>
          <button type="button" @click="toggleHidden(key)" :aria-label="`${isHidden(key) ? 'Show' : 'Hide'} ${labelFor(key)}`" :aria-pressed="isHidden(key)" :class="ARROW">
            <svg v-if="isHidden(key)" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" :d="EYE_OFF"/></svg>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" :d="EYE"/><path stroke-linecap="round" stroke-linejoin="round" :d="PUPIL"/></svg>
          </button>
          <button type="button" :disabled="index === 0" @click="move(index, -1)" :aria-label="`Move ${labelFor(key)} up`" :class="ARROW">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/></svg>
          </button>
          <button type="button" :disabled="index === keys.length - 1" @click="move(index, 1)" :aria-label="`Move ${labelFor(key)} down`" :class="ARROW">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/></svg>
          </button>
        </li>
      </template>
    </draggable>

    <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">A section with nothing in it is not shown on the resume. Use the eye to hide one you do not want, and show it again later.</p>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import draggable from 'vuedraggable'
import { SECTION_LABELS } from './sectionDefs.js'
import { SECTION_ICONS as ICONS } from './sectionIcons.js'
import { moveKey, sectionKeys, withPinned } from './sectionOrder.js'

const ARROW = 'w-7 h-7 flex-shrink-0 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50'
const EYE = 'M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z'
const PUPIL = 'M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z'
const EYE_OFF = 'M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88'
const LOCK = 'M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z'

// The resume's content is edited in place (settings.section_order is what is saved and what the JSON shows).
const props = defineProps({ content: { type: Object, required: true } })

// The sections that can be moved. Dropping a row writes the new order back to the resume's settings, keeping basics first.
const keys = computed({
  get: () => sectionKeys(props.content),
  set: (next) => { props.content.settings.section_order = withPinned(next) },
})

const keyOf = (key) => key
const customOf = (key) => props.content.custom.find((section) => section.id === key)
const labelFor = (key) => SECTION_LABELS[key] || customOf(key)?.title || 'Untitled section'
const iconFor = (key) => ICONS[key] || ICONS.custom
const isHidden = (key) => props.content.settings.hidden_sections.includes(key)

function isEmpty(key) {
  if (key === 'summary') return !props.content.summary.trim()
  return (customOf(key)?.entries || props.content[key] || []).length === 0
}

// Why a row may not show on the resume.
const note = (key) => (isHidden(key) ? 'Hidden' : isEmpty(key) ? 'Empty' : '')

function toggleHidden(key) {
  const hidden = props.content.settings.hidden_sections
  if (hidden.includes(key)) hidden.splice(hidden.indexOf(key), 1)
  else hidden.push(key)
}

function move(index, step) {
  keys.value = moveKey(keys.value, index, index + step)
}
</script>

<style scoped>
.section-row-ghost { opacity: 0.45; border-style: dashed; }
.section-row-chosen { box-shadow: 0 6px 18px -6px rgba(15, 23, 42, 0.25); }
.section-row-drag { box-shadow: 0 10px 24px -8px rgba(15, 23, 42, 0.35); }
</style>
